/**
 * KpiCard — canonical v2 KPI card (v2 Dashboard, Phase 1b fidelity rebuild).
 *
 * Anatomy per design/v2-direction/index.html `.kpi` + DASHBOARD_FIDELITY_SPEC.md §2:
 *   1. Head row (label + delta badge)
 *   2. Metric (28px / 680 / tabular-nums) + optional unit suffix
 *   3. Sparkline (40px, full-width)
 *   4. Footer sublabel
 *
 * Canonical padding: 16px 18px 14px (not the shadcn 24px card default).
 * Uses shadcn Card shell; overrides padding and shadow to match spec density.
 *
 * Delta badge states (spec §2.2):
 *   pos  → hsl(--strong) green + up-triangle ▲
 *   flat → hsl(--muted-foreground) + ± symbol
 *   neg  → hsl(--weak) red + down-triangle ▼
 * Token discipline: all colors via CSS var tokens, no hex in this file.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { KpiSparkline } from "@/components/v2/KpiSparkline";

type DeltaState = "pos" | "flat" | "neg";

interface KpiCardProps {
  /** Canonical label text. LOCKED — e.g. "Interviews this week" must not be reworded. */
  label: string;
  /**
   * Primary metric string. Pass "—" when value is unavailable (honest blank).
   * Use ReactNode only when a composed value is needed.
   */
  metric: string | ReactNode;
  /** Optional unit suffix rendered in a muted smaller span (e.g. " / 30d"). */
  unit?: string;
  /**
   * Delta badge content string, e.g. "23%", "2", "±0", "72%". Pass `null`
   * (or omit) to SUPPRESS the badge entirely — there's no honest delta to show
   * (e.g. when `metric` is the em-dash blank, a "▲2" over no value contradicts
   * itself). No value → no delta.
   */
  delta?: string | null;
  /** Delta badge state. Drives color + triangle symbol. Ignored when no delta. */
  deltaState?: DeltaState;
  /** Footer sublabel text. Static Phase-1 copy acceptable (spec FLAG 2.b). */
  foot: string;
  /** Sparkline data points. [] = honest flat line (never fabricated). */
  sparkPts: number[];
  /** CSS var name for sparkline color, e.g. "--chart-2". */
  sparkCvar?: string;
  /**
   * North-star emphasis (mockup `.kpi.north`). When true the card gets the
   * brand ring/border and the head row leads with a "North-star" tag (the label
   * drops to its own line just beneath). `--brand` is a genuine HSL triple so
   * `hsl(var(--brand) / …)` is valid under [data-theme="v2"] (unlike the flat
   * hex-backed shadcn vars). Off by default — additive, non-breaking.
   */
  north?: boolean;
  /**
   * Optional help affordance rendered immediately after the label (e.g. a
   * `<MetricHelp>` "?" trigger). Kept generic so the card stays presentational.
   */
  help?: ReactNode;
  className?: string;
}

/**
 * Delta badge — canonical `.delta` class anatomy.
 * Pill: 22px tall, 8px padding, 999px radius, 11.5px/600, tabular-nums,
 * 1px solid --border, bg --background. Triangle icon 11×11.
 */
function DeltaBadge({
  text,
  state,
}: {
  text: string;
  state: DeltaState;
}): JSX.Element {
  const colorClass =
    state === "pos"
      ? "text-[hsl(var(--strong))]"
      : state === "neg"
        ? "text-[hsl(var(--weak))]"
        : "text-text-secondary";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-[3px]",
        "h-[22px] rounded-full border border-border-default bg-surface-canvas",
        "px-2 text-[11.5px] font-semibold tabular-nums",
        colorClass,
      )}
    >
      {state === "pos" && (
        <svg
          width="11"
          height="11"
          viewBox="0 0 11 11"
          aria-hidden="true"
          fill="currentColor"
        >
          <path d="M5.5 2 L9 9 L2 9 Z" />
        </svg>
      )}
      {state === "neg" && (
        <svg
          width="11"
          height="11"
          viewBox="0 0 11 11"
          aria-hidden="true"
          fill="currentColor"
        >
          <path d="M5.5 9 L9 2 L2 2 Z" />
        </svg>
      )}
      {text}
    </span>
  );
}

export function KpiCard({
  label,
  metric,
  unit,
  delta,
  deltaState,
  foot,
  sparkPts,
  sparkCvar = "--chart-1",
  north = false,
  help,
  className,
}: KpiCardProps): JSX.Element {
  const labelSpan = (
    <span className="inline-flex items-center gap-[5px]">
      <span
        className="text-[13px] font-medium text-text-secondary"
        style={{ fontWeight: 500 }}
      >
        {label}
      </span>
      {help}
    </span>
  );

  return (
    <div
      className={cn(
        // Canonical card: bg --card, 1px --border, --radius, --shadow-sm
        "rounded-[var(--radius)] border border-border-default bg-surface-card",
        "shadow-[var(--shadow-sm)]",
        // Canonical KPI padding: 16px 18px 14px — not the shadcn 24px card default
        "px-[18px] pt-[16px] pb-[14px]",
        "flex flex-col",
        className,
      )}
      // North-star ring (mockup `.kpi.north`): brand-tinted border + 1px inset
      // ring, keeping the base --shadow-sm. --brand is an HSL triple → valid.
      style={
        north
          ? {
              borderColor: "hsl(var(--brand) / 0.5)",
              boxShadow: "0 0 0 1px hsl(var(--brand) / 0.28), var(--shadow-sm)",
            }
          : undefined
      }
    >
      {/* 1. Head row: (north-tag | label) + delta badge (badge suppressed when no delta) */}
      <div className="flex items-center justify-between gap-2">
        {north ? (
          <span
            className="uppercase"
            style={{
              fontSize: "10.5px",
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: "hsl(var(--brand))",
            }}
          >
            North-star
          </span>
        ) : (
          labelSpan
        )}
        {delta != null && delta !== "" && (
          <DeltaBadge text={delta} state={deltaState ?? "flat"} />
        )}
      </div>

      {/* North variant drops the label to its own line just beneath the tag. */}
      {north && <div style={{ marginTop: "2px" }}>{labelSpan}</div>}

      {/* 2. Metric: 28px / 680 / letter-spacing -0.03em / tabular-nums */}
      <div
        className="tabular-nums leading-[1.05]"
        style={{
          fontSize: "28px",
          fontWeight: 680,
          letterSpacing: "-0.03em",
          marginTop: "8px",
          color: "var(--text-primary)",
        }}
      >
        {metric}
        {unit && (
          <span
            className="text-text-secondary"
            style={{ fontSize: "15px", fontWeight: 500, letterSpacing: 0 }}
          >
            {unit}
          </span>
        )}
      </div>

      {/* 3. Sparkline: 40px tall, full-width, margin-top 10px */}
      <div style={{ marginTop: "10px" }}>
        <KpiSparkline pts={sparkPts} cvar={sparkCvar} h={40} />
      </div>

      {/* 4. Footer sublabel: 12px, --muted-foreground, margin-top 6px */}
      <div
        className="text-text-secondary"
        style={{ fontSize: "12px", marginTop: "6px" }}
      >
        {foot}
      </div>
    </div>
  );
}
